import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TaskBodyProps } from '../index';
import { ReviewSurface } from './ReviewSurface';
import type { VersionsFetchState } from './VersionedPdfPane';
import {
    fetchClinicalDocVersions,
    SDI_VERSION_CONFIG,
    RDI_VERSION_CONFIG,
    type ClinicalDocConfig
} from './clinicalDocVersions';

const VERSIONS_STALE_MS = 60_000;

interface ClinicalDocReviewTaskProps extends TaskBodyProps {
    config: ClinicalDocConfig;
    docLabel: string;
    queryKeyPrefix: string;
}

// View-only admin surface for GP-owned "Pending patient appointment update"
// steps: no internal-actor edges to act on (the practice confirms delivery
// itself), so this only shows the clinical doc + sidebar - no actions card.
function ClinicalDocReviewTask({
    workCase: c,
    onPayloadChange,
    config,
    docLabel,
    queryKeyPrefix
}: ClinicalDocReviewTaskProps) {
    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: true, bodyOwnsSubmit: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {
        data: result,
        isLoading,
        isError,
        refetch
    } = useQuery({
        queryKey: [queryKeyPrefix, c.id],
        queryFn: () => fetchClinicalDocVersions(c.id, config),
        staleTime: VERSIONS_STALE_MS,
        enabled: Boolean(c.id)
    });

    const versionsState: VersionsFetchState = isError ? 'error' : isLoading ? 'loading' : 'ready';

    return (
        <ReviewSurface
            patientId={c.id}
            versions={result?.versions ?? []}
            hasMoreVersions={result?.hasMoreVersions ?? false}
            docLabel={docLabel}
            versionsState={versionsState}
            onRetryVersions={() => void refetch()}
        />
    );
}

export function SdiReviewTask(props: TaskBodyProps) {
    return (
        <ClinicalDocReviewTask
            {...props}
            config={SDI_VERSION_CONFIG}
            docLabel="SDI"
            queryKeyPrefix="sdi-review-versions"
        />
    );
}

export function RdiReviewTask(props: TaskBodyProps) {
    return (
        <ClinicalDocReviewTask
            {...props}
            config={RDI_VERSION_CONFIG}
            docLabel="RDI"
            queryKeyPrefix="rdi-review-versions"
        />
    );
}
